import apiClient from './client';

export interface DashboardStats {
  total_users: number;
  linked_users: number;
  decks_completed_total: number;
  decks_completed_today: number;
  decks_completed_week: number;
  revenue_total_uzs: number;
  revenue_today_uzs: number;
  revenue_week_uzs: number;
  cost_usd_total: number;
  cost_usd_today: number;
  paying_users: number;
  conversion_pct: number;
}

export interface AdminUserRow {
  id: string;
  username: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  telegramId: string | null;
  telegramUsername: string | null;
  telegramLinkedAt: string | null;
  decks_remaining: number;
  decks_used_total: number;
  free_decks_granted: number;
  total_spent_uzs: number;
  last_seen: string | null;
  created_at: string;
}

export type SlidesJobStatus =
  | 'draft'
  | 'awaiting_topic'
  | 'awaiting_meta'
  | 'generating_plan'
  | 'plan_ready'
  | 'approved'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface AdminJobRow {
  id: string;
  user_id: string;
  username: string | null;
  status: SlidesJobStatus;
  topic: string | null;
  type: string | null;
  slide_count: number | null;
  language: string | null;
  level: string | null;
  subject: string | null;
  result_pptx_url: string | null;
  tokens_input: number;
  tokens_output: number;
  cost_usd: string;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface AdminJobSlide {
  id: string;
  job_id: string;
  position: number;
  intent: string | null;
  title: string;
  bullets: string[] | null;
  speaker_notes: string | null;
  image_query: string | null;
  image_url: string | null;
  created_at: string;
}

export interface JobDetail {
  job: AdminJobRow;
  slides: AdminJobSlide[];
}

export interface TopicRow {
  topic: string;
  count: number;
}

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export type SlidesTransactionReason =
  | 'purchase'
  | 'generation'
  | 'generation_refund'
  | 'free_signup'
  | 'talabaa_link_bonus'
  | 'admin_adjustment';

export interface AdminTransactionRow {
  id: string;
  delta: number;
  reason: SlidesTransactionReason;
  related_job_id: string | null;
  related_purchase_id: string | null;
  note: string | null;
  created_at: string;
  balance_after: number;
}

export interface UserTransactionsResult {
  items: AdminTransactionRow[];
  total: number;
  page: number;
  limit: number;
  current_balance: number;
}

export interface SlidesPackage {
  id: string;
  code: string;
  name_uz: string;
  name_ru: string;
  decks_included: number;
  price_uzs: number;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

export interface CreateSlidesPackageDto {
  code: string;
  name_uz: string;
  name_ru: string;
  decks_included: number;
  price_uzs: number;
  is_active?: boolean;
  display_order?: number;
}

export type UpdateSlidesPackageDto = Partial<CreateSlidesPackageDto>;

export const slidesPackagesApi = {
  getAll: async (includeInactive = false): Promise<SlidesPackage[]> => {
    const { data } = await apiClient.get<SlidesPackage[]>(
      '/admin/slides/packages',
      { params: { includeInactive: includeInactive ? 'true' : undefined } },
    );
    return data;
  },

  create: async (dto: CreateSlidesPackageDto): Promise<SlidesPackage> => {
    const { data } = await apiClient.post<SlidesPackage>(
      '/admin/slides/packages',
      dto,
    );
    return data;
  },

  update: async (
    id: string,
    dto: UpdateSlidesPackageDto,
  ): Promise<SlidesPackage> => {
    const { data } = await apiClient.patch<SlidesPackage>(
      `/admin/slides/packages/${id}`,
      dto,
    );
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/slides/packages/${id}`);
  },
};

export const slidesBotApi = {
  stats: async (): Promise<DashboardStats> => {
    const { data } = await apiClient.get<DashboardStats>('/admin/slides/stats');
    return data;
  },

  listUsers: async (params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PageResult<AdminUserRow>> => {
    const { data } = await apiClient.get<PageResult<AdminUserRow>>(
      '/admin/slides/users',
      { params },
    );
    return data;
  },

  listJobs: async (params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<PageResult<AdminJobRow>> => {
    const { data } = await apiClient.get<PageResult<AdminJobRow>>(
      '/admin/slides/jobs',
      { params },
    );
    return data;
  },

  getJob: async (id: string): Promise<JobDetail> => {
    const { data } = await apiClient.get<JobDetail>(`/admin/slides/jobs/${id}`);
    return data;
  },

  topTopics: async (limit = 50): Promise<TopicRow[]> => {
    const { data } = await apiClient.get<TopicRow[]>('/admin/slides/topics', {
      params: { limit },
    });
    return data;
  },

  userTransactions: async (
    userId: string,
    params: {
      page?: number;
      limit?: number;
      reason?: SlidesTransactionReason;
      from?: string;
      to?: string;
    } = {},
  ): Promise<UserTransactionsResult> => {
    const { data } = await apiClient.get<UserTransactionsResult>(
      `/admin/slides/users/${userId}/transactions`,
      { params },
    );
    return data;
  },
};
